import React, { useState, useEffect, useRef } from 'react';
import { Card, ToggleSwitchField, Button } from '@dopomogai/ui';
import { Eye, EyeOff, Bug, Trash2, RefreshCw } from 'lucide-react';
import type { WidgetConfig, DebugLogEntry } from '../types/widget';

interface PreviewPanelProps {
  config: WidgetConfig;
  templateId: string | null;
  apiBaseUrl: string;
  isLoading?: boolean;
}

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  config,
  templateId,
  apiBaseUrl,
  isLoading = false,
}) => {
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [debugLogs, setDebugLogs] = useState<DebugLogEntry[]>([]);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Generate preview URL
  const getPreviewUrl = () => {
    if (!templateId) return 'about:blank';
    
    const params = new URLSearchParams({
      template_id: templateId,
      debug: isDebugMode ? '1' : '0',
      preview: '1',
    });
    
    return `${apiBaseUrl}/widget-preview?${params.toString()}`;
  };

  // Update widget config via postMessage
  useEffect(() => {
    if (iframeRef.current && templateId) {
      const iframe = iframeRef.current;
      const message = {
        type: 'UPDATE_CONFIG',
        payload: config,
      };
      
      // Wait for iframe to load before sending message
      const sendMessage = () => {
        try {
          iframe.contentWindow?.postMessage(message, '*');
        } catch (error) {
          console.warn('Failed to send config update to iframe:', error);
        }
      };

      if (iframe.contentDocument?.readyState === 'complete') {
        sendMessage();
      } else {
        iframe.addEventListener('load', sendMessage);
        return () => iframe.removeEventListener('load', sendMessage);
      }
    }
  }, [config, templateId]);

  // Listen for debug messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'DEBUG_LOG' && isDebugMode) {
        const logEntry: DebugLogEntry = {
          timestamp: Date.now(),
          event: event.data.payload,
          type: event.data.payload?.type || 'unknown',
        };
        setDebugLogs(prev => [...prev, logEntry]);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isDebugMode]);

  const refreshPreview = () => {
    setPreviewKey(prev => prev + 1);
    setDebugLogs([]);
  };

  const clearDebugLogs = () => {
    setDebugLogs([]);
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  };

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Preview Controls */}
      <Card noPadding={false}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-slate-900">Live Preview</h3>
            <ToggleSwitchField
              label="Debug Mode"
              checked={isDebugMode}
              onChange={(e) => setIsDebugMode(e.target.checked)}
              labelPosition="right"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              icon={<RefreshCw className="w-4 h-4" />}
              onClick={refreshPreview}
              disabled={isLoading}
            />
          </div>
        </div>
      </Card>

      {/* Preview Container */}
      <Card noPadding className="flex-1 min-h-0">
        <div className="h-full flex flex-col">
          {/* Status Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                isLoading ? 'bg-amber-500 animate-pulse' : 
                templateId ? 'bg-emerald-500' : 'bg-slate-400'
              }`} />
              <span className="text-sm text-slate-600">
                {isLoading ? 'Loading...' : templateId ? 'Widget Active' : 'No Template Selected'}
              </span>
            </div>
            
            {isDebugMode && (
              <div className="flex items-center gap-2">
                <Bug className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-blue-600">Debug Mode</span>
              </div>
            )}
          </div>

          {/* Preview Iframe */}
          <div className="flex-1 min-h-0 relative">
            {templateId ? (
              <iframe
                key={previewKey}
                ref={iframeRef}
                src={getPreviewUrl()}
                className="w-full h-full border-0"
                title="Widget Preview"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                <div className="text-center">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Save your configuration to see a live preview</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Debug Console */}
      {isDebugMode && (
        <Card 
          title="Debug Console" 
          icon={<Bug className="w-5 h-5" />}
          noPadding={false}
          actionButton={
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 className="w-4 h-4" />}
              onClick={clearDebugLogs}
              disabled={debugLogs.length === 0}
            />
          }
        >
          <div className="bg-slate-900 rounded-lg p-4 max-h-64 overflow-y-auto">
            {debugLogs.length === 0 ? (
              <div className="text-slate-400 text-sm text-center py-8">
                No debug logs yet. Interact with the widget to see debug information.
              </div>
            ) : (
              <div className="space-y-2">
                {debugLogs.map((log, index) => (
                  <div key={index} className="text-xs font-mono">
                    <span className="text-slate-400">
                      [{formatTimestamp(log.timestamp)}]
                    </span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      log.type === 'error' ? 'bg-red-900 text-red-200' :
                      log.type === 'message_chunk' ? 'bg-blue-900 text-blue-200' :
                      log.type === 'tool_call_start' ? 'bg-purple-900 text-purple-200' :
                      log.type === 'tool_response_end' ? 'bg-green-900 text-green-200' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      {log.type}
                    </span>
                    <div className="text-slate-300 mt-1 ml-4">
                      <pre className="whitespace-pre-wrap break-words">
                        {JSON.stringify(log.event, null, 2)}
                      </pre>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};